import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { CarouselModule, OwlOptions } from 'ngx-owl-carousel-o';
import { GamesLandingComponent } from './games-landing/games-landing.component';
import { TopWinnersComponent } from './top-winners/top-winners.component';
import { Router, RouterLink } from '@angular/router';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { MusicService } from '../../Services/music.service';
import { LotteryCardsComponent } from './lottery-cards/lottery-cards.component';
import { LotteryWinnersComponent } from './lottery-winners/lottery-winners.component';
import { HowItWorkComponent } from './how-it-work/how-it-work.component';
import { LandingPageService } from './landing-page.service';
import { LoaderComponent } from '../../components/loader/loader.component';
import { GameService } from '../../Services/game.service';
import { PwaInstallService } from '../../Services/pwa-install.service';
import { ToastrService } from 'ngx-toastr';
import { ApiCallService } from '../../Services/api-call-service.service';
import { ErrorhandlingService } from '../../Services/error-handling.service';
import { LoaderService } from '../../Services/loader-service.service';

@Component({
  selector: 'app-landing-page',
  standalone: true,
  imports: [
    CarouselModule,
    CommonModule,
    ReactiveFormsModule,
    GamesLandingComponent,
    TopWinnersComponent,
    LoaderComponent,
    RouterLink,
  ],
  templateUrl: './landing-page.component.html',
  styleUrl: './landing-page.component.scss',
})
export class LandingPageComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private toastr = inject(ToastrService);
  public pwaInstall = inject(PwaInstallService);
  public showInstallButton: boolean = true;
  isMobile: boolean = false;
  isLandingPage: boolean = true;
  activeLotteries: any = [];

  contactForm: FormGroup;

  constructor(
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private musicService: MusicService,
    private landingPageService: LandingPageService,
    private gameService: GameService,
    private fb: FormBuilder,
    private apiService: ApiCallService,
    private errorHandler: ErrorhandlingService,
    private loaderService: LoaderService
  ) {
    this.Games = this.gameService.getGames();
    this.contactForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: [
        '',
        [
          Validators.required,
          Validators.email,
          Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/),
        ],
      ],
      message: ['', Validators.required],
    });
  }

  onContactSubmit() {
    if (this.contactForm.valid) {
      this.loaderService.show();
      const formValue = this.contactForm.value;
      const payload = {
        userName: `${formValue.firstName} ${formValue.lastName}`,
        userEmail: formValue.email,
        message: formValue.message,
      };

      this.apiService
        .PostCallWithoutToken(payload, 'Public/UserContactUs')
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (response) => {
            this.loaderService.hide();
            if (response.responseCode === 200) {
              this.toastr.success(response.responseMessage, 'Success');
              this.contactForm.reset();
            } else {
              this.errorHandler.handleResponseError(response);
            }
          },
          (error) => {
            this.loaderService.hide();
            this.errorHandler.handleHttpError(error);
          }
        );
    } else {
      this.contactForm.markAllAsTouched();
      this.toastr.warning('Please fill in all required fields.', 'Warning');
    }
  }

  hideInstallBtn(event: Event) {
    event.stopPropagation();
    this.showInstallButton = false;
  }
  Games: any = [];
  private interactionStarted = false;
  ngOnInit(): void {
    this.isLandingPage = this.router.url === '/';

    // Observe screen size changes
    this.breakpointObserver
      .observe([Breakpoints.Handset])
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.isMobile = result.matches;
      });

    setTimeout(() => {
      this.startMusic();
    }, 3000);

    //? Add interaction listeners
    this.addInteractionListeners();

    this.landingPageService.getActiveLotteries().pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.activeLotteries = data;
    });
  }

  isMobileMenuOpen = false;
  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  RedirectToLogin(): void {
    this.router.navigate(['/login']);
  }

  get isToken(): boolean {
    return (
      typeof localStorage !== 'undefined' && !!localStorage.getItem('token')
    );
  }

  RedirectToSignUp(): void {
    if (this.isToken) {
      this.router.navigate(['/dashboard/home']);
    } else {
      this.router.navigate(['/SignUp']);
    }
  }

  goToDashboard(): void {
    if (this.isToken) {
      this.router.navigate(['/dashboard/home']);
    } else {
      this.router.navigate(['/login']);
    }
  }

  landingSpinAmount: number = 5;

  incrementLandingSpinAmount(): void {
    this.landingSpinAmount = Math.min(20, this.landingSpinAmount + 5);
  }

  decrementLandingSpinAmount(): void {
    this.landingSpinAmount = Math.max(5, this.landingSpinAmount - 5);
  }

  SpinToLogin(): void {
    if (this.isToken) {
      this.router.navigate(['/dashboard/spinner']);
    } else {
      this.router.navigate(['/login'], {
        queryParams: { redirectUrl: '/dashboard/spinner' },
      });
    }
  }

  async installApp(): Promise<void> {
    const result = await this.pwaInstall.promptInstall();

    if (result === 'unavailable') {
      this.toastr.info(
        'To install: open your browser menu (⋮ or …) → "Install app" or "Add to Home Screen".',
        'Install Luckycrest',
        { timeOut: 6000, positionClass: 'toast-top-center' }
      );
    } else if (result === 'accepted') {
      this.toastr.success('App installed successfully!', 'Luckycrest', {
        timeOut: 3000,
      });
    }
  }

  activeNav: string = 'home';

  scrollTo(id: string) {
    const element = document.getElementById(id);
    if (element) {
      const header = document.querySelector('header');
      const headerOffset = (header?.getBoundingClientRect().height ?? 80) + 16;
      const y =
        element.getBoundingClientRect().top + window.pageYOffset - headerOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      this.activeNav = id;
    }
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.activeNav = 'home';
  }

  addInteractionListeners() {
    // Browser only: no document during server-side rendering.
    if (typeof document === 'undefined') return;
    if (!this.interactionStarted) {
      document.addEventListener('click', this.startMusicOnce, { once: true });
      document.addEventListener('mousemove', this.startMusicOnce, {
        once: true,
      });
      document.addEventListener('keydown', this.startMusicOnce, { once: true });
      this.interactionStarted = true;
    }
  }

  startMusicOnce = () => {
    this.startMusic();
    this.removeInteractionListeners();
  };

  removeInteractionListeners() {
    // Browser only: an uncaught error here during server rendering crashed `ng serve`.
    if (typeof document === 'undefined') return;
    document.removeEventListener('mousemove', this.startMusicOnce);
    document.removeEventListener('scroll', this.startMusicOnce);
    document.removeEventListener('click', this.startMusicOnce);
  }

  ngOnDestroy() {
    this.removeInteractionListeners();
    this.destroy$.next();
    this.destroy$.complete();
  }

  startMusic() {
    this.musicService.playMusic();
  }

  toggleMusic() {
    this.musicService.muteUnmute();
  }

  get isMusicMuted(): boolean {
    return this.musicService.isMusicMuted();
  }

  openFaqIndex: number | null = 0;
  selectedFaqCategory: string = 'all';
  footerSubscribed: boolean = false;

  subscribeFooter(email: string): void {
    const trimmed = (email || '').trim();
    if (!trimmed || !trimmed.includes('@') || !trimmed.includes('.')) {
      this.toastr.warning('Please enter a valid email address.', 'Email Required');
      return;
    }
    this.footerSubscribed = true;
    this.toastr.success(
      'You have joined the Luckycrest VIP Lounge! Check your inbox for exclusive perk vouchers.',
      'VIP Perks Activated!'
    );
  }

  faqs: Array<{ question: string; answer: string; category: string; badge: string }> = [
    // PAYMENTS
    {
      category: 'payments',
      badge: 'Redemptions',
      question: 'How do I withdraw my winnings or redeem rewards?',
      answer:
        'Open your wallet from the top dashboard, select "Redeem", and choose your preferred payout method (crypto, bank transfer, or gift card). Once your account KYC is verified, approved redemptions are processed promptly within 24-48 hours.',
    },
    {
      category: 'payments',
      badge: 'Purchases',
      question: 'What payment methods can I use to purchase coin packages?',
      answer:
        'We support all major credit/debit cards (Visa, Mastercard, Discover), secure direct banking, and popular cryptocurrencies including Bitcoin (BTC), Ethereum (ETH), and USDT. All transactions are protected by bank-level 256-bit encryption.',
    },
    {
      category: 'payments',
      badge: 'Zero Fees',
      question: 'Are there any hidden fees on deposits or withdrawals?',
      answer:
        'Luckycrest does not charge any hidden fees for coin purchases or redemptions. You receive full value for your transactions. Standard blockchain gas fees or your local card issuer foreign transaction policies may apply.',
    },
    {
      category: 'payments',
      badge: 'Instant Delivery',
      question: 'How long does it take for purchased coins to reflect in my wallet?',
      answer:
        'Purchased coin packs are credited instantly to your account wallet as soon as payment is confirmed by your bank or the blockchain network. If you ever experience any delay, refresh your balance or contact our 24/7 support desk.',
    },

    // BONUSES
    {
      category: 'bonuses',
      badge: 'Welcome Pack',
      question: 'What welcome bonus do new players receive upon joining?',
      answer:
        'Every new player at Luckycrest receives an instant Welcome Pack upon registration and email verification. This includes free Gold Coins and Sweeps Coins so you can explore all games right away with zero deposit required.',
    },
    {
      category: 'bonuses',
      badge: 'Daily Streak',
      question: 'How do daily login bonuses and streak multipliers work?',
      answer:
        'Log in every 24 hours to claim your daily bonus. Each consecutive day you log in, your streak bonus increases up to 7 days, unlocking escalating coin packages, free spins, and scratch cards.',
    },
    {
      category: 'bonuses',
      badge: 'VIP Rewards',
      question: 'What is the Luckycrest VIP Club and how do I rank up?',
      answer:
        'Every spin and play earns you VIP Experience Points (XP). As your XP builds, you progress through Bronze, Silver, Gold, Platinum, and Diamond tiers. Higher tiers unlock higher redemption limits, weekly rakeback, birthday gifts, and dedicated VIP hosts.',
    },
    {
      category: 'bonuses',
      badge: 'Promo Codes',
      question: 'Where do I enter promotional bonus codes?',
      answer:
        'Go to your Wallet or Account Menu, click "Promo Voucher", type in your promotional code, and tap Apply. Your bonus coins or tickets are deposited into your vault immediately.',
    },

    // MY ACCOUNT
    {
      category: 'account',
      badge: 'Account Access',
      question: 'How do I login to my Luckycrest Game account?',
      answer:
        'Tap "Login" at the top of the page and enter the email and password you registered with. If you have forgotten your password, use the "Forgot Password" link and we will email you a secure one-time reset link.',
    },
    {
      category: 'account',
      badge: 'Free Membership',
      question: 'Are there membership fees for Luckycrest Game?',
      answer:
        'No. Creating an account is completely free. There are never any membership or recurring subscription fees. You only ever pay for optional coin packages, and every new player starts with a welcome bonus on the house.',
    },
    {
      category: 'account',
      badge: 'KYC & Safety',
      question: 'How do I complete identity (KYC) verification?',
      answer:
        'Navigate to Profile > Verification. Upload a government-issued photo ID (Passport, Driver’s License) and a proof of address (utility bill within 90 days). Verification is reviewed and approved swiftly by our security team.',
    },
    {
      category: 'account',
      badge: 'Security',
      question: 'Can I update my email address or personal details?',
      answer:
        'You can update profile preferences directly in your Account Settings. For security-sensitive changes like registered email or legal name, please submit a quick ticket to our verified support team.',
    },

    // GAMES
    {
      category: 'games',
      badge: 'Mobile Ready',
      question: 'Can I play the casino games on mobile devices?',
      answer:
        'Yes. Luckycrest runs directly in your mobile browser on both iOS and Android, and you can install it to your home screen via our PWA install prompt for a full-screen, native app-like experience.',
    },
    {
      category: 'games',
      badge: 'Provably Fair',
      question: 'Are the game outcomes fair and certified?',
      answer:
        'All Luckycrest games utilize industry-standard, cryptographically secure Random Number Generators (RNG) tested by independent auditors to guarantee 100% fair and unbiased outcomes on every single spin, card deal, and roll.',
    },
    {
      category: 'games',
      badge: 'Auto Save',
      question: 'What happens if my connection drops or a game freezes?',
      answer:
        'Every round state and bet is securely recorded on our game servers in real time. If your internet disconnects, simply reopen the game and the round will either resume or automatically settle and credit any winnings to your balance.',
    },
    {
      category: 'games',
      badge: 'Game Library',
      question: 'What types of games are available on Luckycrest?',
      answer:
        'Luckycrest offers a rich portfolio of high-energy games including classic & video slots, Plinko, Crash/Aviator, Roulette, Baccarat, Royal Match, instant Scratch & Win cards, and our exclusive Lucky Wheel.',
    },
  ];

  get filteredFaqs(): Array<{ question: string; answer: string; category: string; badge: string }> {
    if (this.selectedFaqCategory === 'all') {
      return this.faqs;
    }
    return this.faqs.filter((faq) => faq.category === this.selectedFaqCategory);
  }

  faqColumns: Array<Array<{ faq: any; index: number }>> = [
    this.faqs
      .map((faq, index) => ({ faq, index }))
      .filter((_, i) => i % 2 === 0),
    this.faqs
      .map((faq, index) => ({ faq, index }))
      .filter((_, i) => i % 2 === 1),
  ];

  setFaqCategory(category: string): void {
    if (this.selectedFaqCategory === category) {
      this.selectedFaqCategory = 'all';
    } else {
      this.selectedFaqCategory = category;
    }
    this.updateFaqColumns();
    this.openFaqIndex = 0;
  }

  updateFaqColumns(): void {
    const list = this.filteredFaqs;
    this.faqColumns = [
      list
        .map((faq, index) => ({ faq, index }))
        .filter((_, i) => i % 2 === 0),
      list
        .map((faq, index) => ({ faq, index }))
        .filter((_, i) => i % 2 === 1),
    ];
  }

  toggleFaq(index: number): void {
    this.openFaqIndex = this.openFaqIndex === index ? null : index;
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByFaqEntryIndex(index: number, entry: { index: number; faq?: any }): any {
    return entry.faq ? entry.faq.question : entry.index;
  }

  cardList = [
    {
      id: '01',
      title: 'Rome Treasury',
      subtitle: '$116 Million Win',
      amount: '$20',
      image: 'assets/images/lottery.png',
      time: { hh: '03', mm: '03', ss: '03' },
    },
    {
      id: '02',
      title: 'Paris Vault',
      subtitle: '$90 Million Win',
      amount: '$15',
      image: 'assets/images/lottery.png',
      time: { hh: '01', mm: '15', ss: '42' },
    },
    {
      id: '03',
      title: 'London Luck',
      subtitle: '$75 Million Win',
      amount: '$10',
      image: 'assets/images/lottery.png',
      time: { hh: '05', mm: '45', ss: '22' },
    },
    {
      id: '04',
      title: 'Tokyo Draw',
      subtitle: '$110 Million Win',
      amount: '$25',
      image: 'assets/images/lottery.png',
      time: { hh: '02', mm: '25', ss: '08' },
    },
    {
      id: '05',
      title: 'Dubai Jackpot',
      subtitle: '$98 Million Win',
      amount: '$30',
      image: 'assets/images/lottery.png',
      time: { hh: '04', mm: '12', ss: '33' },
    },
    {
      id: '06',
      title: 'Vegas Vault',
      subtitle: '$120 Million Win',
      amount: '$40',
      image: 'assets/images/lottery.png',
      time: { hh: '06', mm: '05', ss: '18' },
    },
  ];
}
